import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { money } from "@/lib/format";

export const withShowing = (sections, showing) => (showing ? [{ title: "Showing", head: ["Report filter"], body: [[showing]] }, ...sections] : sections);

export const cellText = (s, i, v, r) => (s.fmt ? s.fmt(v, i, r) : s.moneyCols?.includes(i) && v !== "" && v != null ? money(v) : v ?? "");

const BLACK = [20, 20, 20];
const YELLOW = [255, 241, 118];

function gcCell(d, s) {
  const st = s.rowStyles[d.row.index];
  const c = d.column.index;
  const cell = d.cell.styles;
  if (c >= 2) cell.halign = "right";
  if (c >= 2 && d.cell.raw !== "" && (st === "bfrow" || st === "balance") && c === 4) cell.fillColor = YELLOW;
  if (st === "item" && c === 1) cell.cellPadding = { left: 24, top: 3, bottom: 3, right: 4 };
  if (["head", "bfrow", "group", "subtotal", "grand", "balance"].includes(st)) cell.fontStyle = "bold";
}

function gcLines(d, s, doc) {
  if (d.section !== "body" || d.column.index < 2 || d.cell.raw === "") return;
  const st = s.rowStyles[d.row.index];
  const { x, y, width: w, height: h } = d.cell;
  const line = (yy) => doc.line(x + 3, yy, x + w - 3, yy);
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.6);
  const top = st === "subtotal" || st === "grand" || (st === "bfrow" && d.column.index === 4);
  if (top) line(y + 0.5);
  line(y + h - 2.5);
  if (st === "grand") line(y + h - 0.5);
}

function gcTable(doc, s, y) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFontSize(13);
  doc.setFont(undefined, "bold");
  s.title.split(" - ").forEach((line, i) => doc.text(line, w / 2, y + 14 + i * 17, { align: "center" }));
  doc.setFont(undefined, "normal");
  autoTable(doc, {
    startY: y + 62, showHead: false, theme: "plain",
    body: s.body.map((r, ri) => r.map((v, i) => String(cellText(s, i, v, ri)))),
    styles: { fontSize: 9, cellPadding: { top: 4, bottom: 5, left: 4, right: 4 }, textColor: BLACK },
    columnStyles: { 0: { cellWidth: 24 }, 2: { cellWidth: 90 }, 3: { cellWidth: 90 }, 4: { cellWidth: 100 } },
    margin: { left: 60, right: 60 },
    didParseCell: (d) => gcCell(d, s),
    didDrawCell: (d) => gcLines(d, s, doc),
  });
}

export function buildPDF(title, subtitle, sections, sig) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  doc.setFontSize(16);
  doc.text(title, 40, 40);
  doc.setFontSize(9);
  doc.text(subtitle, 40, 56);
  let y = 72;
  sections.forEach((s) => {
    if (s.rowStyles) {
      gcTable(doc, s, y);
      doc.addPage();
      y = 40;
      return;
    }
    if (y > 500) { doc.addPage(); y = 40; }
    doc.setFontSize(12);
    doc.text(s.title, 40, y + 8);
    autoTable(doc, {
      startY: y + 16,
      head: [s.head],
      body: s.body.map((r) => r.map((v, i) => String(cellText(s, i, v)))),
      foot: s.foot ? [s.foot.map((v, i) => String(cellText(s, i, v)))] : undefined,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [78, 19, 30] },
      footStyles: { fillColor: [253, 246, 227], textColor: 20, fontStyle: "bold" },
      margin: { left: 40, right: 40 },
      didParseCell: (d) => {
        if (d.section !== "body" && s.moneyCols?.includes(d.column.index)) d.cell.styles.halign = "right";
        if (d.section === "body" && s.strong?.includes(d.row.index)) { d.cell.styles.fontStyle = "bold"; d.cell.styles.fillColor = [253, 246, 227]; }
      },
      columnStyles: Object.fromEntries((s.moneyCols || []).map((i) => [i, { halign: "right" }])),
    });
    y = doc.lastAutoTable.finalY + 26;
  });
  drawSignatures(doc, sig, y);
  return doc;
}

function drawSignatures(doc, sig, y) {
  if (!sig) return;
  const w = doc.internal.pageSize.getWidth();
  if (y > doc.internal.pageSize.getHeight() - 130) { doc.addPage(); y = 50; }
  const colW = (w - 120) / 2;
  sig.forEach((p, i) => {
    const x = 40 + i * (colW + 40);
    if (p.img) {
      try {
        const pr = doc.getImageProperties(p.img);
        const ih = 45;
        doc.addImage(p.img, "PNG", x, y, Math.min(colW, (pr.width * ih) / pr.height), ih);
      } catch (e) { /* skip unreadable image */ }
    }
    doc.setDrawColor(100);
    doc.line(x, y + 52, x + colW, y + 52);
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(p.name || "", x, y + 66);
    doc.setFont(undefined, "normal");
    doc.setFontSize(9);
    if (p.date) doc.text(`Date: ${p.date}`, x + colW, y + 66, { align: "right" });
    if (p.title) doc.text(p.title, x, y + 78);
    doc.setFontSize(8);
    doc.text(p.label.toUpperCase(), x, y + (p.title ? 90 : 78));
  });
}

export function exportPDF(filename, title, subtitle, sections, sig) {
  buildPDF(title, subtitle, sections, sig).save(filename);
}

export const pdfBlob = (title, subtitle, sections, sig) => buildPDF(title, subtitle, sections, sig).output("blob");

export async function exportImage(el, filename, type) {
  const { default: html2canvas } = await import("html2canvas");
  // html2canvas measures font baselines with a probe <img> in the LIVE document; Tailwind's img{display:block} breaks it
  const probe = document.createElement("style");
  probe.textContent = 'body > div[style*="visibility: hidden"] > img[width="1"]{display:inline !important}';
  document.head.appendChild(probe);
  const canvas = await html2canvas(el, {
    scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: 1300,
    onclone: (doc) => {
      const node = doc.querySelector("[data-annual-doc]");
      const st = doc.createElement("style");
      st.textContent = "img{display:inline-block !important}";
      doc.head.appendChild(st);
      node.style.width = "1200px";
      node.querySelectorAll(".overflow-x-auto").forEach((e) => { e.style.overflow = "visible"; });
    },
  }).finally(() => probe.remove());
  const a = document.createElement("a");
  a.href = canvas.toDataURL(type === "jpeg" ? "image/jpeg" : "image/png", 0.95);
  a.download = filename;
  a.click();
}

function buildWorkbook(sections, sig) {
  const wb = XLSX.utils.book_new();
  if (sig) {
    const ws = XLSX.utils.aoa_to_sheet([["", "Name", "Title", "Date", "Signature"], ...sig.map((p) => [p.label, p.name, p.title, p.date || "", p.img ? "Signed (see PDF)" : ""])]);
    ws["!cols"] = [{ wch: 14 }, { wch: 30 }, { wch: 30 }, { wch: 18 }];
    wb.__sig = ws;
  }
  sections.forEach((s) => {
    if (s.rowStyles) {
      const ws = XLSX.utils.aoa_to_sheet([...s.title.split(" - ").map((t) => ["", t]), [], ...s.body]);
      Object.keys(ws).forEach((k) => { if (ws[k]?.t === "n") ws[k].z = "#,##0.00"; });
      ws["!cols"] = [{ wch: 4 }, { wch: 44 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, ws, "Financial Report");
      return;
    }
    const ws = XLSX.utils.aoa_to_sheet([s.head, ...s.body, ...(s.foot ? [s.foot] : [])]);
    XLSX.utils.book_append_sheet(wb, ws, s.title.replace(/[\\/?*[\]:]/g, "").slice(0, 31));
  });
  if (wb.__sig) { XLSX.utils.book_append_sheet(wb, wb.__sig, "Signatures"); delete wb.__sig; }
  return wb;
}

export function exportXLSX(filename, sections, sig) {
  XLSX.writeFile(buildWorkbook(sections, sig), filename);
}

export const xlsxBlob = (sections, sig) => new Blob([XLSX.write(buildWorkbook(sections, sig), { type: "array", bookType: "xlsx" })],
  { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
